import React, { useEffect, useId, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  Divider,
  IconButton,
  Typography,
  useMediaQuery
} from '@mui/material';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  srOnlySx,
  dialogPaperSx,
  outerWrapSx,
  scrollableContentSx,
  innerContentSx,
  successIconSx,
  titleSx,
  subtitleSx,
  dividerSx,
  sectionTitleSx,
  cardsContainerSx,
  cardSx,
  cardIconSx,
  cardTitleSx,
  cardDescriptionSx,
  footerSx,
  carouselClipSx,
  navGroupSx,
  navBtnSx,
  navGroupWrapperSx,
  cardListItemSx,
  cardTextContainerSx,
  CARD_WIDTH_PX,
  CARD_GAP_PX
} from './AccountSubmittedModal.styles';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CardItem {
  id: string;
  title: string;
  description: string;
  img: string;
  alt: string;
  url: string;
}

interface AccountSubmittedModalProps {
  open: boolean;
  onClose: () => void;
}

// ─── Slide-up transition (matches existing pattern) ───────────────────────────

const SlideUp = React.forwardRef(function SlideUp(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return (
    <Slide
      direction="up"
      ref={ref}
      {...props}
      easing={{
        enter: 'cubic-bezier(0.22, 1, 0.36, 1)',
        exit: 'cubic-bezier(0.32, 0.72, 0, 1)'
      }}
      timeout={{ enter: 600, exit: 300 }}
    />
  );
});

// ─── Component ────────────────────────────────────────────────────────────────

const AccountSubmittedModal: React.FC<AccountSubmittedModalProps> = ({
  open,
  onClose
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Unique IDs for aria-labelledby / aria-describedby
  const baseId = useId();
  const titleId = `${baseId}-title`;
  const subtitleId = `${baseId}-subtitle`;
  const announcementId = `${baseId}-announcement`;

  // Ref for the return button – receives focus when the modal opens
  const returnBtnRef = useRef<HTMLButtonElement>(null);

  // ── Carousel state ──────────────────────────────────────────────────────────
  const [startIndex, setStartIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState<number | null>(null);
  const [dragOffset, setDragOffset] = useState(0);

  const isMobile = useMediaQuery('(max-width:600px)');
  const isTablet = useMediaQuery('(min-width:601px) and (max-width:1023px)');

  // Move focus into the modal once it is visible (WCAG 2.4.3 Focus Order)
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        returnBtnRef.current?.focus();
      }, 650); // after the slide-up transition completes
      return () => clearTimeout(timer);
    }
  }, [open]);

  // ── Data from i18n ──────────────────────────────────────────────────────────
  const cardsRaw = t('registrationStepsData.accountSubmittedModal.cards', {
    returnObjects: true
  });
  const cards: CardItem[] = Array.isArray(cardsRaw) ? cardsRaw : [];

  // ── Carousel calculations ───────────────────────────────────────────────────
  const VISIBLE_CARDS = isMobile ? 1 : isTablet ? 2 : 3;
  const CARD_OFFSET = CARD_WIDTH_PX + CARD_GAP_PX;
  const totalCards = cards.length;
  const maxShift = Math.max(0, totalCards - VISIBLE_CARDS);
  const canNavigate = maxShift > 0;
  const dragThreshold = CARD_OFFSET * 0.25;

  // ── Carousel handlers ───────────────────────────────────────────────────────
  const handleNext = () => {
    setDragOffset(0);
    setStartIndex((prev) => Math.min(prev + 1, maxShift));
  };

  const handlePrev = () => {
    setDragOffset(0);
    setStartIndex((prev) => Math.max(prev - 1, 0));
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (!canNavigate) return;
    setIsDragging(true);
    setDragStartX(e.clientX);
    setDragOffset(0);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!isDragging || dragStartX === null) return;
    const delta = e.clientX - dragStartX;
    if (
      Math.abs(delta) > 5 &&
      !(e.currentTarget as HTMLElement).hasPointerCapture(e.pointerId)
    ) {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    }
    if (startIndex === 0 && delta > 0) setDragOffset(delta * 0.35);
    else if (startIndex === maxShift && delta < 0) setDragOffset(delta * 0.35);
    else setDragOffset(delta);
  };

  const handleDragEnd = (e: React.PointerEvent) => {
    if (!isDragging) return;
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    setIsDragging(false);
    if (dragOffset <= -dragThreshold) handleNext();
    else if (dragOffset >= dragThreshold) handlePrev();
    else setDragOffset(0);
    setDragStartX(null);
  };

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleReturnHome = () => {
    onClose();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(`/${i18n.language}/`);
  };

  const handleCardNavigate = (url: string) => {
    onClose();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(`/${i18n.language}${url}`);
  };

  const handleCardKeyDown = (
    event: React.KeyboardEvent<HTMLAnchorElement>,
    url: string
  ) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleCardNavigate(url);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <Dialog
      open={open}
      fullScreen
      TransitionComponent={SlideUp}
      aria-labelledby={titleId}
      aria-describedby={subtitleId}
      aria-modal="true"
      PaperProps={{ sx: dialogPaperSx }}
      onClose={onClose}
    >
      {/*
       * WCAG 4.1.3 Status Messages – polite live region announces the
       * confirmation as soon as the modal mounts.
       */}
      <Box
        id={announcementId}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        sx={srOnlySx}
      >
        {open
          ? t('registrationStepsData.accountSubmittedModal.liveAnnouncement')
          : ''}
      </Box>

      <Box sx={outerWrapSx}>
        {/* ── Scrollable content ──────────────────────────────────────────── */}
        <Box component="main" sx={scrollableContentSx}>
          <Box sx={innerContentSx}>
            {/* Success icon (decorative – title conveys the result) */}
            <CheckCircleIcon
              sx={successIconSx}
              aria-hidden="true"
              focusable="false"
            />

            {/* Title */}
            <Typography id={titleId} component="h1" sx={titleSx}>
              {t('registrationStepsData.accountSubmittedModal.pageTitle')}
            </Typography>

            {/* Subtitle */}
            <Typography id={subtitleId} sx={subtitleSx}>
              {t('registrationStepsData.accountSubmittedModal.subtitle')}
            </Typography>

            <Divider sx={dividerSx} aria-hidden="true" />

            {/* Section title – centred, exactly as original */}
            <Typography component="h2" sx={sectionTitleSx}>
              {t('registrationStepsData.accountSubmittedModal.sectionTitle')}
            </Typography>

            {/*
             * Carousel: clip wrapper hides off-screen cards while preserving
             * box-shadows. The ul is the sliding transform track.
             */}
            <Box sx={carouselClipSx}>
              <Box
                component="ul"
                role="list"
                aria-label={t(
                  'registrationStepsData.accountSubmittedModal.cardsListLabel'
                )}
                sx={{
                  ...cardsContainerSx,
                  transition: isDragging
                    ? 'none'
                    : 'transform 0.5s ease-in-out',
                  transform: `translateX(calc(-${startIndex * CARD_OFFSET}px + ${dragOffset}px))`
                }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={handleDragEnd}
                onPointerCancel={handleDragEnd}
              >
                {cards.map((card) => (
                  <Box
                    key={card.id}
                    component="li"
                    role="listitem"
                    sx={cardListItemSx}
                  >
                    <Box
                      component="a"
                      href={`/${i18n.language}${card.url}`}
                      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                        e.preventDefault();
                        handleCardNavigate(card.url);
                      }}
                      onKeyDown={(e: React.KeyboardEvent<HTMLAnchorElement>) =>
                        handleCardKeyDown(e, card.url)
                      }
                      aria-labelledby={`card-title-${card.id}`}
                      aria-describedby={`card-desc-${card.id}`}
                      sx={cardSx}
                    >
                      {/* Card icon */}
                      <Box
                        component="img"
                        src={card.img}
                        alt={card.alt}
                        sx={cardIconSx}
                      />

                      <Box sx={cardTextContainerSx}>
                        {/* Card title */}
                        <Typography
                          id={`card-title-${card.id}`}
                          component="h3"
                          sx={cardTitleSx}
                        >
                          {card.title}
                        </Typography>

                        {/* Card description */}
                        <Typography
                          id={`card-desc-${card.id}`}
                          sx={cardDescriptionSx}
                        >
                          {card.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* ── Carousel navigation (keyboard users) ────────────────────── */}
            {/* {canNavigate && (
              <Box sx={navGroupWrapperSx}>
                <Box sx={navGroupSx}>
                  <IconButton
                    onClick={handlePrev}
                    disabled={startIndex === 0}
                    aria-label={t(
                      'registrationStepsData.accountSubmittedModal.prevButtonAriaLabel'
                    )}
                    sx={navBtnSx}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <IconButton
                    onClick={handleNext}
                    disabled={startIndex === maxShift}
                    aria-label={t(
                      'registrationStepsData.accountSubmittedModal.nextButtonAriaLabel'
                    )}
                    sx={navBtnSx}
                  >
                    <ArrowForwardIcon />
                  </IconButton>
                </Box>
              </Box>
            )} */}
          </Box>
        </Box>

        {/* ── Sticky footer ───────────────────────────────────────────────── */}
        <Box component="footer" sx={footerSx}>
          <Button
            ref={returnBtnRef}
            variant="primary"
            size="md"
            onClick={handleReturnHome}
            aria-label={t(
              'registrationStepsData.accountSubmittedModal.returnButtonAriaLabel'
            )}
            disableElevation
          >
            {t('registrationStepsData.accountSubmittedModal.returnButton')}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default AccountSubmittedModal;
